import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Upload, FileText, Brain, BookOpen, ListChecks,
  HelpCircle, BarChart3, Loader2, Check, Sparkles, CreditCard,
  Crown, Trash2, ExternalLink, ShoppingBag, X, Camera, PenTool, GraduationCap, Pencil
} from 'lucide-react';
import HandwrittenUpload from '../components/HandwrittenUpload';
import AddToCourseModal from '../components/AddToCourseModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = ['Computer Science', 'Biology', 'Chemistry', 'Physics', 'Mathematics', 'Engineering', 'Medicine', 'Business', 'Psychology', 'Literature', 'History', 'General'];

const StudyBuilder = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [processingDoc, setProcessingDoc] = useState(null);

  // Rename state
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Upload tab
  const [uploadTab, setUploadTab] = useState('document');

  // List for sale modal
  const [listModal, setListModal] = useState(null);
  // Add to course modal
  const [courseModal, setCourseModal] = useState(null);
  const [listForm, setListForm] = useState({
    title: '', description: '', field: 'General', price: '',
    preview_content: '', document_type: 'Notes', tags: ''
  });

  useEffect(() => {
    fetchDocuments();
    handlePaymentCallback();
  }, []);

  const handlePaymentCallback = async () => {
    const paymentStatus = searchParams.get('payment');
    const subStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');
    const docId = searchParams.get('doc_id');

    if (sessionId && (paymentStatus === 'success' || subStatus === 'success')) {
      try {
        const res = await axios.get(`${API}/study-builder/payment-status/${sessionId}`);
        if (res.data.status === 'paid' || res.data.status === 'active') {
          toast.success(res.data.type === 'subscription'
            ? 'Subscription activated! You now have unlimited AI study generation.'
            : 'Payment successful! Generating your study materials...');

          if (docId && res.data.type === 'one_time') {
            await triggerGeneration(docId);
          }
          await fetchDocuments();
        }
      } catch (e) {
        toast.error('Failed to verify payment');
      }
      // Clean URL
      window.history.replaceState({}, '', '/study-builder');
    } else if (paymentStatus === 'canceled' || subStatus === 'canceled') {
      toast.info('Payment canceled');
      window.history.replaceState({}, '', '/study-builder');
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/study-builder/documents`);
      setDocuments(res.data.documents || []);
      setHasSubscription(res.data.has_subscription || false);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/study-builder/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`"${res.data.title}" uploaded successfully!`);
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files[0]);
  };

  const handlePayment = async (docId) => {
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/study-builder/checkout/${docId}`, formData);
      if (res.data.already_subscribed) {
        toast.success('Subscriber benefit applied! Generating study materials...');
        await triggerGeneration(docId);
        return;
      }
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    }
  };

  const handleSubscribe = async () => {
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/study-builder/subscribe`, formData);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start subscription');
    }
  };

  const triggerGeneration = async (docId) => {
    setProcessingDoc(docId);
    try {
      const res = await axios.post(`${API}/study-builder/generate/${docId}`);
      if (res.data.status === 'completed') {
        toast.success('Study materials generated!');
        await fetchDocuments();
        setProcessingDoc(null);
        return;
      }
      // Generation running in background — poll until done
      toast.info('Generating study materials... This may take a few minutes for large documents.');
      const poll = setInterval(async () => {
        try {
          const check = await axios.get(`${API}/study-materials/${docId}`);
          if (check.data.status === 'completed') {
            clearInterval(poll);
            toast.success('Study materials ready!');
            await fetchDocuments();
            setProcessingDoc(null);
          } else if (check.data.status === 'failed') {
            clearInterval(poll);
            toast.error('Generation failed. Please try again.');
            await fetchDocuments();
            setProcessingDoc(null);
          }
        } catch {
          // 402/404 during poll just means not ready yet — keep polling
        }
      }, 4000);
      // Allow up to 5 minutes for large documents
      setTimeout(() => {
        clearInterval(poll);
        setProcessingDoc(null);
        fetchDocuments();
      }, 300000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
      setProcessingDoc(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document and all its study materials?')) return;
    try {
      const res = await axios.delete(`${API}/study-builder/documents/${docId}`);
      toast.success(res.data?.message || 'Document deleted');
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete document');
    }
  };

  const startRename = (doc) => {
    setRenamingDoc(doc.id);
    setRenameValue(doc.title);
  };

  const handleRename = async (docId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenamingDoc(null); return; }
    try {
      await axios.put(`${API}/documents/${docId}`, { title: trimmed });
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, title: trimmed } : d));
      toast.success('Title updated');
    } catch (e) {
      toast.error('Failed to rename');
    }
    setRenamingDoc(null);
  };

  const openListModal = (doc) => {
    setListForm({
      title: doc.title, description: '', field: 'General',
      price: '', preview_content: '', document_type: 'Notes', tags: ''
    });
    setListModal(doc);
  };

  const handleListForSale = async () => {
    if (!listModal) return;
    if (!listForm.title || !listForm.description || !listForm.price || !listForm.preview_content) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const formData = new FormData();
      Object.entries(listForm).forEach(([k, v]) => formData.append(k, v));
      await axios.post(`${API}/study-builder/list-for-sale/${listModal.id}`, formData);
      toast.success('Document listed on marketplace!');
      setListModal(null);
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to list');
    }
  };

  const getStatusBadge = (doc) => {
    const s = doc.status;
    if (s === 'completed') return <span className="px-2.5 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Ready</span>;
    if (s === 'generating' || processingDoc === doc.id) return <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Generating</span>;
    if (s === 'paid') return <span className="px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Paid</span>;
    if (s === 'failed') return <span className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Failed</span>;
    return <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Uploaded</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button data-testid="back-btn" onClick={() => navigate('/')} variant="ghost" className="mb-6 rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-700 rounded-2xl p-6 md:p-10 mb-8 text-white relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/15 backdrop-blur rounded-xl flex items-center justify-center">
                <Brain className="w-7 h-7" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Crimson Text, serif' }}>
                AI Study Builder
              </h1>
            </div>
            <p className="text-blue-100 text-base md:text-lg max-w-2xl">
              Upload your notes and let AI create a complete study dashboard — summaries, flashcards, quizzes, and progress tracking.
            </p>
            {hasSubscription && (
              <div className="mt-4 flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full w-fit">
                <Crown className="w-4 h-4 text-yellow-300" />
                <span className="text-sm font-medium">Unlimited Plan Active</span>
              </div>
            )}
          </div>
        </div>

        {/* Features Preview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { icon: BookOpen, label: 'AI Summary', desc: 'Key takeaways' },
            { icon: ListChecks, label: 'Key Terms', desc: '15+ definitions' },
            { icon: Brain, label: 'Flashcards', desc: '20+ cards' },
            { icon: HelpCircle, label: 'Quiz', desc: '10+ questions' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
              <Icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
          {/* Tab Switcher */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5">
            <button
              data-testid="tab-document"
              onClick={() => setUploadTab('document')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                uploadTab === 'document'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
            <button
              data-testid="tab-handwritten"
              onClick={() => setUploadTab('handwritten')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                uploadTab === 'handwritten'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <PenTool className="w-4 h-4" />
              Scan Handwritten Notes
            </button>
          </div>

          {uploadTab === 'document' ? (
            <>
              <div
                data-testid="study-builder-dropzone"
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tiff,.bmp,.webp"
                  onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
                    <p className="text-gray-700 font-medium">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                      <Upload className="w-7 h-7 text-blue-600" />
                    </div>
                    <p className="text-base font-medium text-gray-700 mb-1">Drag & drop your notes here</p>
                    <p className="text-sm text-gray-500 mb-2">or click to browse</p>
                    <p className="text-xs text-gray-400">PDF, Word, TXT, or images (max 50MB)</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">
                Uploading is free. You only pay when you generate the AI study dashboard.
              </p>
            </>
          ) : (
            <HandwrittenUpload
              onDocumentCreated={fetchDocuments}
              hasSubscription={hasSubscription}
            />
          )}
        </div>

        {/* Pricing */}
        {!hasSubscription && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Pricing</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-800">Per Document</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">$3<span className="text-base font-normal text-gray-500">/upload</span></p>
                <p className="text-sm text-gray-500">Pay only for what you need. One-time fee per document.</p>
              </div>
              <div className="border-2 border-blue-500 rounded-xl p-5 bg-blue-50/50 relative">
                <div className="absolute -top-3 right-4 bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium">Best Value</div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-800">Unlimited Plan</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">$10<span className="text-base font-normal text-gray-500">/month</span></p>
                <p className="text-sm text-gray-500 mb-3">Unlimited AI study generation. Cancel anytime.</p>
                <Button
                  data-testid="subscribe-btn"
                  onClick={handleSubscribe}
                  className="w-full rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  <Crown className="w-4 h-4 mr-2" /> Subscribe Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Documents List */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">My Study Documents</h2>
          {documents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No documents yet. Upload your first notes above!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <div key={doc.id} data-testid={`study-doc-${doc.id}`} className="p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      doc.is_handwritten ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      {doc.is_handwritten
                        ? <PenTool className="w-5 h-5 text-orange-600" />
                        : <FileText className="w-5 h-5 text-blue-600" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      {renamingDoc === doc.id ? (
                        <Input
                          data-testid={`rename-input-${doc.id}`}
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => handleRename(doc.id)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(doc.id); if (e.key === 'Escape') setRenamingDoc(null); }}
                          autoFocus
                          className="rounded-lg text-sm font-medium h-8 max-w-xs"
                        />
                      ) : (
                        <div className="flex items-center gap-1.5 group/title">
                          <p className="font-medium text-gray-800 truncate">{doc.title}</p>
                          <button
                            data-testid={`rename-btn-${doc.id}`}
                            onClick={() => startRename(doc)}
                            className="text-gray-300 hover:text-blue-600 transition-colors opacity-0 group-hover/title:opacity-100 flex-shrink-0"
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-xs text-gray-500 truncate max-w-[150px] sm:max-w-none">
                          {doc.is_handwritten ? 'Handwritten Notes (OCR)' : doc.file_name}
                        </p>
                        {getStatusBadge(doc)}
                        {(doc.listed_for_sale || doc.is_marketplace) && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">On Marketplace</span>
                        )}
                      </div>
                    </div>
                    {/* Desktop actions */}
                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                      <Button data-testid={`view-btn-${doc.id}`} onClick={() => navigate(`/document/${doc.id}`)} size="sm" variant="outline" className="rounded-lg">
                        <ExternalLink className="w-4 h-4 mr-1" /> View
                      </Button>
                      {doc.status === 'completed' && (
                        <>
                          <Button data-testid={`study-btn-${doc.id}`} onClick={() => navigate(`/study/${doc.id}`)} size="sm" className="rounded-lg bg-blue-600 hover:bg-blue-700">
                            <Brain className="w-4 h-4 mr-1" /> Study
                          </Button>
                          <Button data-testid={`course-btn-${doc.id}`} onClick={() => setCourseModal(doc)} size="sm" variant="outline" className="rounded-lg border-blue-200 text-blue-700">
                            <GraduationCap className="w-4 h-4 mr-1" /> Course
                          </Button>
                          {!doc.listed_for_sale && !doc.is_marketplace && (
                            <Button data-testid={`list-btn-${doc.id}`} onClick={() => openListModal(doc)} size="sm" variant="outline" className="rounded-lg">
                              <ShoppingBag className="w-4 h-4 mr-1" /> Sell
                            </Button>
                          )}
                        </>
                      )}
                      {doc.status === 'uploaded' && !doc.paid && (!doc.is_handwritten || doc.ocr_status === 'confirmed') && (
                        <Button data-testid={`generate-btn-${doc.id}`} onClick={() => hasSubscription ? triggerGeneration(doc.id) : handlePayment(doc.id)} size="sm" className="rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700">
                          <Sparkles className="w-4 h-4 mr-1" /> {hasSubscription ? 'Generate' : '$3 Generate'}
                        </Button>
                      )}
                      {doc.is_handwritten && doc.ocr_status === 'pending' && (
                        <span className="text-xs text-gray-400 italic">Awaiting OCR</span>
                      )}
                      {doc.is_handwritten && doc.ocr_status === 'processing' && (
                        <span className="text-xs text-amber-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Processing OCR</span>
                      )}
                      {doc.status === 'paid' && (
                        <Button data-testid={`gen-paid-btn-${doc.id}`} onClick={() => triggerGeneration(doc.id)} size="sm" className="rounded-lg bg-blue-600 hover:bg-blue-700">
                          <Sparkles className="w-4 h-4 mr-1" /> Generate Now
                        </Button>
                      )}
                      {doc.status === 'failed' && (
                        <Button data-testid={`retry-btn-${doc.id}`} onClick={() => triggerGeneration(doc.id)} size="sm" variant="outline" className="rounded-lg border-red-200 text-red-600">Retry</Button>
                      )}
                      {(processingDoc === doc.id || doc.status === 'generating') && (
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                          <Loader2 className="w-4 h-4 animate-spin" /><span>Generating...</span>
                        </div>
                      )}
                      <Button data-testid={`delete-btn-${doc.id}`} onClick={() => handleDelete(doc.id)} size="sm" variant="ghost" className="rounded-lg text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Mobile actions */}
                  <div className="flex sm:hidden items-center gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                    <Button data-testid={`view-btn-m-${doc.id}`} onClick={() => navigate(`/document/${doc.id}`)} size="sm" variant="outline" className="rounded-lg flex-1">
                      <ExternalLink className="w-4 h-4 mr-1" /> View
                    </Button>
                    {doc.status === 'completed' && (
                      <>
                        <Button data-testid={`study-btn-m-${doc.id}`} onClick={() => navigate(`/study/${doc.id}`)} size="sm" className="rounded-lg bg-blue-600 hover:bg-blue-700 flex-1">
                          <Brain className="w-4 h-4 mr-1" /> Study
                        </Button>
                        <Button data-testid={`course-btn-m-${doc.id}`} onClick={() => setCourseModal(doc)} size="sm" variant="outline" className="rounded-lg border-blue-200 text-blue-700 flex-1">
                          <GraduationCap className="w-4 h-4 mr-1" /> Course
                        </Button>
                        {!doc.listed_for_sale && !doc.is_marketplace && (
                          <Button data-testid={`list-btn-m-${doc.id}`} onClick={() => openListModal(doc)} size="sm" variant="outline" className="rounded-lg flex-1">
                            <ShoppingBag className="w-4 h-4 mr-1" /> Sell
                          </Button>
                        )}
                      </>
                    )}
                    {doc.status === 'uploaded' && !doc.paid && (!doc.is_handwritten || doc.ocr_status === 'confirmed') && (
                      <Button data-testid={`generate-btn-m-${doc.id}`} onClick={() => hasSubscription ? triggerGeneration(doc.id) : handlePayment(doc.id)} size="sm" className="rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 flex-1">
                        <Sparkles className="w-4 h-4 mr-1" /> {hasSubscription ? 'Generate' : '$3 Generate'}
                      </Button>
                    )}
                    {doc.status === 'paid' && (
                      <Button data-testid={`gen-paid-btn-m-${doc.id}`} onClick={() => triggerGeneration(doc.id)} size="sm" className="rounded-lg bg-blue-600 hover:bg-blue-700 flex-1">
                        <Sparkles className="w-4 h-4 mr-1" /> Generate Now
                      </Button>
                    )}
                    {doc.status === 'failed' && (
                      <Button data-testid={`retry-btn-m-${doc.id}`} onClick={() => triggerGeneration(doc.id)} size="sm" variant="outline" className="rounded-lg border-red-200 text-red-600 flex-1">Retry</Button>
                    )}
                    {(processingDoc === doc.id || doc.status === 'generating') && (
                      <div className="flex items-center gap-2 text-sm text-amber-600 flex-1 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /><span>Generating...</span>
                      </div>
                    )}
                    <Button data-testid={`delete-btn-m-${doc.id}`} onClick={() => handleDelete(doc.id)} size="sm" variant="ghost" className="rounded-lg text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List for Sale Modal */}
      {listModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">List on Marketplace</h3>
              <Button onClick={() => setListModal(null)} variant="ghost" size="icon" className="rounded-xl">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <Input value={listForm.title} onChange={e => setListForm({...listForm, title: e.target.value})} className="rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <Textarea value={listForm.description} onChange={e => setListForm({...listForm, description: e.target.value})} rows={3} className="rounded-xl" placeholder="Describe what this document covers..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field *</label>
                  <Select value={listForm.field} onValueChange={v => setListForm({...listForm, field: v})}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELDS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD) *</label>
                  <Input type="number" step="0.01" min="0.01" value={listForm.price} onChange={e => setListForm({...listForm, price: e.target.value})} className="rounded-xl" placeholder="9.99" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preview Content *</label>
                <Textarea value={listForm.preview_content} onChange={e => setListForm({...listForm, preview_content: e.target.value})} rows={3} className="rounded-xl" placeholder="Excerpt visible before purchase..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <Input value={listForm.tags} onChange={e => setListForm({...listForm, tags: e.target.value})} className="rounded-xl" placeholder="calculus, notes (comma-separated)" />
              </div>
              <Button data-testid="confirm-list-btn" onClick={handleListForSale} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700">
                <ShoppingBag className="w-4 h-4 mr-2" /> List for Sale
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Course Modal */}
      {courseModal && (
        <AddToCourseModal
          documentId={courseModal.id}
          documentTitle={courseModal.title}
          onClose={() => setCourseModal(null)}
          onSuccess={() => toast.success('Document added to course!')}
        />
      )}
    </div>
  );
};

export default StudyBuilder;
